import api from '../utils/api';
import { db, clearReplica } from './db';
import { ENTITIES, TABLE_BY_PULL_KEY, TABLE_BY_ENTITY_TYPE } from './entities';

const PAGE_LIMIT = 500;
const INITIAL_SYNC_COMPLETE_KEY = 'initialSyncComplete';
const VIEWABLE_KEY = 'viewablePermissions';

/** Thrown when an offline pull can't write a page because the device is out of
 *  storage. Typed so the provider can surface a "free space / stay online"
 *  banner instead of silently swallowing it as a generic network error. (B6) */
export class SyncStorageFullError extends Error {
  constructor(message = 'Device storage is full — could not finish downloading offline data.') {
    super(message);
    this.name = 'SyncStorageFullError';
  }
}

/** True for an IndexedDB quota error — raw (`name`) or wrapped by Dexie inside a
 *  transaction (`inner.name`). (B6) */
export function isQuotaError(e: any): boolean {
  return e?.name === 'QuotaExceededError' || e?.inner?.name === 'QuotaExceededError';
}

export async function getLastSync(): Promise<string | undefined> {
  return (await db.meta.get('lastSyncTimestamp'))?.value;
}

/** True only after the FIRST full pull has finished. Until then the replica may
 *  be partial (some tables filled, others empty) and must NOT be served as
 *  authoritative. Reset implicitly by clearReplica (logout / clinic-or-user
 *  switch), which wipes meta. (B7) */
export async function isInitialSyncComplete(): Promise<boolean> {
  return (await db.meta.get(INITIAL_SYNC_COMPLETE_KEY))?.value === true;
}
async function getStoredClinic(): Promise<string | undefined> {
  return (await db.meta.get('clinicId'))?.value;
}
async function getStoredUser(): Promise<string | undefined> {
  return (await db.meta.get('userId'))?.value;
}

/**
 * Upsert every entity array present in a /sync/changes page into its table.
 * Additive-pull safeguard: a row with an un-synced local write (its id is in
 * `dirtyIds`) is NOT overwritten — the optimistic version stays until the op is
 * pushed, where any conflict is detected and surfaced. Without this, a colleague's
 * online edit re-delivered in the delta would clobber the user's pending edit.
 */
async function upsertResponse(data: any, dirtyIds: Set<string>): Promise<void> {
  for (const [key, table] of Object.entries(TABLE_BY_PULL_KEY)) {
    if (Array.isArray(data[key]) && data[key].length) {
      const rows = dirtyIds.size
        ? data[key].filter((r: any) => !dirtyIds.has(r.id))
        : data[key];
      if (rows.length) {
        try {
          await (db as any)[table].bulkPut(rows);
        } catch (e) {
          // A quota failure leaves a PARTIAL replica; surface a typed error so
          // the watermark stays put (safe re-pull) and the UI can warn. (B6)
          if (isQuotaError(e)) throw new SyncStorageFullError();
          throw e;
        }
      }
    }
  }
  if (data.clinic) await db.clinic.put(data.clinic);
}

/** Apply server-side deletions (the Step-1 tombstone feed) to the replica. */
async function applyDeletions(deletions: any[] | undefined): Promise<void> {
  if (!deletions?.length) return;
  for (const d of deletions) {
    const table = TABLE_BY_ENTITY_TYPE[d.entityType];
    if (table) await (db as any)[table].delete(d.entityId);
  }
}

/**
 * Loop `GET /sync/changes` from `since` (or initial when undefined) until the
 * server stops returning a cursor. The watermark advances ONLY after a complete
 * pull, so an interrupted pull simply re-runs from the last good timestamp
 * (idempotent — bulkPut/delete are safe to repeat).
 */
async function pull(
  clinicId: string,
  since?: string,
  userId?: string,
  viewable?: string[],
): Promise<void> {
  // Cross-clinic AND cross-user guard: a different clinic's data must never
  // linger locally (tenant isolation), and a different USER must not inherit
  // the preserved op-queue (B3 keeps it across a non-logout session end, so a
  // different same-clinic vet could otherwise drain — and be mis-attributed as
  // the author of — another vet's offline writes). A same-user resume keeps the
  // queue (B3's whole point). userId is only compared when both sides are known,
  // so legacy callers that omit it behave exactly as before.
  const storedClinic = await getStoredClinic();
  const storedUser = await getStoredUser();
  const clinicSwitch = !!storedClinic && storedClinic !== clinicId;
  const userSwitch = !!storedUser && !!userId && storedUser !== userId;
  if (clinicSwitch || userSwitch) {
    await clearReplica();
    since = undefined;
  }

  // A view granted since the last good pull covers rows that never changed, so only a full pull
  // brings them (fix 87). No stored set yet counts as gained: views granted before this shipped
  // were missing those rows too.
  if (viewable) {
    const before = await forgetUnviewable(viewable);
    if (since && (!before || viewable.some((slug) => !before.includes(slug)))) since = undefined;
  }

  // Ids with un-synced local writes — computed AFTER the wipe (which clears the
  // queue), so a fresh-clinic/fresh-user pull never skips anything.
  const dirtyIds = new Set((await db.opQueue.toArray()).map((o) => o.entityId));

  let cursor: string | undefined;
  let ts: string | undefined;
  do {
    const params: any = { limit: PAGE_LIMIT };
    if (since && !cursor) params.since = since;
    if (cursor) params.cursor = cursor;
    const { data } = await api.get('/sync/changes', { params });
    await upsertResponse(data, dirtyIds);
    await applyDeletions(data.deletions);
    ts = data.syncTimestamp;
    cursor = data.nextCursor; // backend sets this only while hasMore
  } while (cursor);

  if (ts) await db.meta.put({ key: 'lastSyncTimestamp', value: ts });
  await db.meta.put({ key: 'clinicId', value: clinicId });
  // Record the owning user so a different-user sign-in is detected as a switch.
  if (userId) await db.meta.put({ key: 'userId', value: userId });
  if (viewable) await db.meta.put({ key: VIEWABLE_KEY, value: viewable });
  // Reached ONLY when the do/while exits normally (no throw mid-pull), so an
  // interrupted first pull never marks the replica "ready" to serve. (B7)
  await db.meta.put({ key: INITIAL_SYNC_COMPLETE_KEY, value: true });
}

/**
 * Fix 87 (INV-152): the server leaves out an entity the person may not view, which looks exactly
 * like "no changes", so the device drops what a revoked permission covered itself. Needs no
 * network, so the provider also runs it the moment the set changes, offline included. The stored
 * set keeps only what is still held, so a slug lost here and regained before the next good pull
 * still counts as gained. Returns the set stored before this call.
 */
export async function forgetUnviewable(viewable: string[]): Promise<string[] | undefined> {
  for (const e of ENTITIES) {
    if (e.view && !viewable.includes(e.view)) await (db as any)[e.table].clear();
  }
  const before: string[] | undefined = (await db.meta.get(VIEWABLE_KEY))?.value;
  if (before) await db.meta.put({ key: VIEWABLE_KEY, value: before.filter((slug) => viewable.includes(slug)) });
  return before;
}

/** Full initial download. */
export async function pullAll(clinicId: string, userId?: string): Promise<void> {
  await pull(clinicId, undefined, userId);
}

/**
 * Incremental pull since the last watermark (or full if none yet). `viewable` is the set of view
 * permissions the person holds, once known; without it fix 87's wipe is skipped.
 */
export async function pullDelta(clinicId: string, userId?: string, viewable?: string[]): Promise<void> {
  const since = await getLastSync();
  await pull(clinicId, since, userId, viewable);
}
